import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Collapse,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Slider,
  TextField,
  FormControlLabel,
  Checkbox,
  Stack,
  IconButton,
  Tooltip,
  Snackbar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TablePagination,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  AccountTree as ClusterIcon,
  CheckCircle,
  SkipNext as SkipIcon,
  Tune as TuneIcon,
  Image as ImageIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import imageService from '../../api/imageService';
import AuthenticatedImage from '../AuthenticatedImage';
import ImageAnalysisDetailsDialog from '../ImageAnalysisDetailsDialog';

/**
 * WizardStep4Clustering
 * Props:
 *   session   – current session object
 *   onNext    – advance wizard
 *   onBack    – go back
 */
const WizardStep4Clustering = ({ session, onNext, onBack, onNavConfigChange }) => {
  const sessionId = session?.uniqueId;

  // ── Stats ─────────────────────────────────────────────────────────────────
  const [stats, setStats] = useState(null); // embedding stats
  const [loadingStats, setLoadingStats] = useState(false);

  // ── Existing clusters ─────────────────────────────────────────────────────
  const [clusters, setClusters] = useState(null); // null = not loaded yet
  const [loadingClusters, setLoadingClusters] = useState(false);

  // ── Options ───────────────────────────────────────────────────────────────
  const [opts, setOpts] = useState({
    similarityThreshold: 0.85,
    minClusterSize: 1,
    maxClusters: 20,
  });

  // ── Quality filters (Advanced) ────────────────────────────────────────────
  const [qualityFilters, setQualityFilters] = useState({
    excludeBlurry: true,
    requireAlbumWorthy: true,
    requireSmile: false,
    minQualityScore: 0,
    minEventFitScore: 5,
    excludedEventTypes: [],   // types the user has UNchecked (empty = include all)
    minPersonCount: null,
    maxPersonCount: null,
  });
  // Event types that actually exist in this session (populated from stats.byEventType)
  const [sessionEventTypes, setSessionEventTypes] = useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  // ── Filter summary (shown after clustering completes) ────────────────────
  const [filterSummary, setFilterSummary] = useState(null);
  // ── Unclustered images ───────────────────────────────────────────────────────────────────
  const [unclusteredImages, setUnclusteredImages] = useState(null);
  const [unclusteredPage, setUnclusteredPage] = useState(0);
  const [unclusteredPageSize, setUnclusteredPageSize] = useState(50);
  // ── Image detail dialog ──────────────────────────────────────────────────────────────────
  const [imageDetailDialog, setImageDetailDialog] = useState({ open: false, imageId: null });
  // ── Cluster panel visibility (collapsed by default to avoid loading all thumbnails) ────────
  const [clustersOpen, setClustersOpen] = useState(false);
  const [expandedClusters, setExpandedClusters] = useState(new Set());
  // ── Run state ─────────────────────────────────────────────────────────────
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState(null);

  // ── Snackbar ──────────────────────────────────────────────────────────────
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const showSnackbar = (message, severity = 'info') =>
    setSnackbar({ open: true, message, severity });

  // ─────────────────────────────────────────────────────────────────────────
  // Load stats + clusters on mount
  // ─────────────────────────────────────────────────────────────────────────
  const loadStats = useCallback(async () => {
    if (!sessionId) return;
    setLoadingStats(true);
    try {
      const data = await imageService.getEmbeddingStats(sessionId);
      setStats(data.stats);
      // Populate the list of event types present in this session
      if (data.stats?.byEventType) {
        const types = Object.keys(data.stats.byEventType).sort();
        setSessionEventTypes(types);
      }
      // Sync maxClusters default to actual image count (only if user hasn't manually changed it yet)
      if (data.stats?.totalImages > 0) {
        setOpts((prev) => ({
          ...prev,
          maxClusters: data.stats.totalImages,
        }));
      }
    } catch (err) {
      console.warn('[Step4] Failed to load embedding stats', err);
    } finally {
      setLoadingStats(false);
    }
  }, [sessionId]);

  const loadClusters = useCallback(async () => {
    if (!sessionId) return;
    setLoadingClusters(true);
    try {
      const data = await imageService.getSessionClusters(sessionId);
      setClusters(data.clusters || []);
      // Restore the parameters used in the last clustering run (same behaviour as the
      // image-upload cluster tab: setClusterParams(data.parameters))
      if (data.parameters) {
        setOpts((prev) => ({
          ...prev,
          similarityThreshold: data.parameters.similarityThreshold ?? prev.similarityThreshold,
          minClusterSize: data.parameters.minClusterSize ?? prev.minClusterSize,
          maxClusters: data.parameters.maxClusters ?? prev.maxClusters,
        }));
      }
    } catch (err) {
      console.warn('[Step4] Failed to load clusters', err);
      setClusters([]);
    } finally {
      setLoadingClusters(false);
    }
  }, [sessionId]);

  const loadUnclustered = useCallback(async () => {
    if (!sessionId) return;
    try {
      const data = await imageService.getUnclusteredImages(sessionId);
      setUnclusteredImages(data.unclusteredImages || []);
    } catch (err) {
      console.warn('[Step4] Failed to load unclustered images', err);
      setUnclusteredImages([]);
    }
  }, [sessionId]);

  useEffect(() => {
    loadStats();
    loadClusters();
    loadUnclustered();
  }, [loadStats, loadClusters, loadUnclustered]);

  // ─────────────────────────────────────────────────────────────────────────
  // Run clustering
  // ─────────────────────────────────────────────────────────────────────────
  const handleRunClustering = async () => {
    setRunning(true);
    setRunError(null);
    try {
      // Convert excludedEventTypes (UX concept) → eventTypes include-list (backend concept)
      const { excludedEventTypes, ...restFilters } = qualityFilters;
      const effectiveEventTypes =
        excludedEventTypes.length > 0 && sessionEventTypes.length > 0
          ? sessionEventTypes.filter((t) => !excludedEventTypes.includes(t))
          : [];
      const result = await imageService.clusterSessionImages(sessionId, {
        similarityThreshold: opts.similarityThreshold,
        minClusterSize: opts.minClusterSize,
        maxClusters: opts.maxClusters,
        useMetadata: true,
        filters: { ...restFilters, eventTypes: effectiveEventTypes },
      });

      if (!result.success) {
        setRunError(result.message || 'Clustering failed');
        showSnackbar(result.message || 'Clustering failed', 'warning');
      } else {
        const count = result.clusters?.length || 0;
        showSnackbar(`Clustering complete — ${count} cluster(s) created.`, 'success');
        if (result.stats) setFilterSummary(result.stats);
        await Promise.all([loadStats(), loadClusters(), loadUnclustered()]);
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Clustering failed';
      setRunError(msg);
      showSnackbar('Clustering failed: ' + msg, 'error');
    } finally {
      setRunning(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Derived
  // ─────────────────────────────────────────────────────────────────────────
  const totalEmbeddings = stats?.totalEmbeddings ?? 0;
  const totalImages = stats?.totalImages ?? filterSummary?.totalImages ?? 0;
  const excludedByFilters = filterSummary?.excludedByFilters ?? 0;
  const passedFilters = Math.max(0, totalImages - excludedByFilters);
  const totalClusters = stats?.totalClusters ?? (clusters?.length ?? 0);
  const clusteredImages = stats?.clusteredImages ?? 0;
  const canCluster = totalEmbeddings > 0;
  const hasClusters = totalClusters > 0;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    onNavConfigChange?.({
      nextLabel: 'Continue',
      nextDisabled: false,
      backDisabled: running,
      showSkip: true,
      skipLabel: 'Skip',
    });
  }, [running]); // onNavConfigChange is stable

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* ── Top navigation ──────────────────────────────────────────────────────── */}
      {/* ── Instructions ── */}
      <Box sx={{ px: 2, py: 1.5, mb: 2.5, bgcolor: 'grey.50', borderLeft: '3px solid', borderColor: 'primary.main', borderRadius: '0 8px 8px 0' }}>
        <Typography variant="body2" color="text.secondary">
          Groups similar photos together using their AI embeddings. Each cluster becomes a section
          of the album. Adjust the parameters below and click <strong>Run Clustering</strong>.
        </Typography>
      </Box>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Image Clustering
        </Typography>
        <Tooltip title="Refresh stats">
          <IconButton size="small" onClick={() => { loadStats(); loadClusters(); loadUnclustered(); }} disabled={loadingStats || loadingClusters}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Configuration section header ───────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.5, lineHeight: 1, whiteSpace: 'nowrap' }}>Configuration</Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
      </Box>

      {/* ── Stats bar ───────────────────────────────────────────────────────── */}
      <Paper
        variant="outlined"
        sx={{ p: 1.5, mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', bgcolor: 'action.hover' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ClusterIcon fontSize="small" color="action" />
          <Typography variant="body2" color="text.secondary">Clustering:</Typography>
        </Box>
        {loadingStats ? (
          <CircularProgress size={16} />
        ) : (
          <>
            <Chip size="small" label={`${totalImages} total images`} variant="outlined" />
            <Chip
              size="small"
              label={`${totalEmbeddings} embeddings`}
              color={totalEmbeddings > 0 ? 'primary' : 'default'}
              variant={totalEmbeddings > 0 ? 'filled' : 'outlined'}
            />
            <Chip
              size="small"
              label={`${totalClusters} cluster${totalClusters !== 1 ? 's' : ''}`}
              color={hasClusters ? 'success' : 'default'}
              variant={hasClusters ? 'filled' : 'outlined'}
            />
            {clusteredImages > 0 && (
              <Chip
                size="small"
                label={`${clusteredImages} clustered images`}
                color="secondary"
                variant="filled"
              />
            )}
            {filterSummary && (
              <>
                {excludedByFilters > 0 && (
                  <Chip
                    size="small"
                    label={`${excludedByFilters} excluded by filters`}
                    color="warning"
                    variant="outlined"
                  />
                )}
                <Chip
                  size="small"
                  label={`${passedFilters} passed filters`}
                  color="info"
                  variant="outlined"
                />
                {filterSummary.processingTimeMs && (
                  <Chip
                    size="small"
                    label={`${(filterSummary.processingTimeMs / 1000).toFixed(1)}s`}
                    variant="outlined"
                    title="Clustering duration"
                  />
                )}
              </>
            )}
          </>
        )}
      </Paper>

      {/* ── No embeddings warning ────────────────────────────────────────────── */}
      {!loadingStats && !canCluster && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          No embeddings found for this session. Go back to Step 3 and run AI Analysis first.
        </Alert>
      )}

      {/* ── Already-clustered notice ─────────────────────────────────────────── */}
      {!loadingStats && hasClusters && (
        <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3 }}>
          This session already has <strong>{totalClusters} cluster{totalClusters !== 1 ? 's' : ''}</strong>.
          You can continue or re-run clustering with different parameters.
        </Alert>
      )}

      {/* ── Options ─────────────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TuneIcon fontSize="small" color="action" />
          <Typography variant="subtitle2">Clustering Parameters</Typography>
        </Box>

        <Grid container spacing={2}>
          {/* Similarity threshold */}
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" gutterBottom>
              Similarity Threshold: {(opts.similarityThreshold * 100).toFixed(1)}%
            </Typography>
            <Slider
              value={opts.similarityThreshold}
              min={0.5}
              max={0.99}
              step={0.01}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => `${(v * 100).toFixed(1)}%`}
              onChange={(_, v) => setOpts((o) => ({ ...o, similarityThreshold: v }))}
              disabled={running}
              size="small"
            />
            <Typography variant="caption" color="text.secondary">
              Higher = stricter grouping
            </Typography>
          </Grid>

          {/* Min cluster size */}
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" gutterBottom>
              Min Cluster Size: {opts.minClusterSize}
            </Typography>
            <Slider
              value={opts.minClusterSize}
              min={1}
              max={10}
              step={1}
              marks
              valueLabelDisplay="auto"
              onChange={(_, v) => setOpts((o) => ({ ...o, minClusterSize: v }))}
              disabled={running}
              size="small"
            />
            <Typography variant="caption" color="text.secondary">
              Minimum images per cluster
            </Typography>
          </Grid>

          {/* Max clusters */}
          <Grid item xs={12} sm={4}>
            <Typography variant="caption" gutterBottom>
              Max Clusters: {opts.maxClusters}
            </Typography>
            <Slider
              value={opts.maxClusters}
              min={5}
              max={stats?.totalImages || 50}
              step={5}
              valueLabelDisplay="auto"
              onChange={(_, v) => setOpts((o) => ({ ...o, maxClusters: v }))}
              disabled={running}
              size="small"
            />
            <Typography variant="caption" color="text.secondary">
              Maximum number of clusters (max: {stats?.totalImages || 50})
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* ── Advanced Quality Filters ──────────────────────────────────────────── */}
      <Accordion
        expanded={showAdvancedFilters}
        onChange={() => setShowAdvancedFilters((v) => !v)}
        variant="outlined"
        sx={{ mb: 3 }}
        disabled={running}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon fontSize="small" />
            <Typography variant="subtitle2">
              Advanced Quality Filters
            </Typography>
            {(qualityFilters.excludeBlurry || qualityFilters.requireAlbumWorthy ||
              qualityFilters.minQualityScore > 0 || qualityFilters.minEventFitScore > 0 || qualityFilters.excludedEventTypes.length > 0) && (
              <Chip label="Active" size="small" color="primary" />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Filter out unwanted images before clustering (blurry, low-quality, wrong event type, etc.)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={qualityFilters.excludeBlurry}
                    onChange={(e) => setQualityFilters((f) => ({ ...f, excludeBlurry: e.target.checked }))}
                  />
                }
                label={<Typography variant="body2">Exclude blurry images</Typography>}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={qualityFilters.requireAlbumWorthy}
                    onChange={(e) => setQualityFilters((f) => ({ ...f, requireAlbumWorthy: e.target.checked }))}
                  />
                }
                label={<Typography variant="body2">Album-worthy photos only</Typography>}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={qualityFilters.requireSmile}
                    onChange={(e) => setQualityFilters((f) => ({ ...f, requireSmile: e.target.checked }))}
                  />
                }
                label={<Typography variant="body2">Require smiling faces</Typography>}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" gutterBottom display="block">
                Minimum Quality Score: {qualityFilters.minQualityScore}/10
              </Typography>
              <Slider
                value={qualityFilters.minQualityScore}
                min={0}
                max={10}
                step={1}
                marks
                valueLabelDisplay="auto"
                size="small"
                onChange={(_, v) => setQualityFilters((f) => ({ ...f, minQualityScore: v }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" gutterBottom display="block">
                Min Event Fit Score: {qualityFilters.minEventFitScore}/10
              </Typography>
              <Slider
                value={qualityFilters.minEventFitScore}
                min={0}
                max={10}
                step={1}
                marks
                valueLabelDisplay="auto"
                size="small"
                onChange={(_, v) => setQualityFilters((f) => ({ ...f, minEventFitScore: v }))}
              />
              <Typography variant="caption" color="text.secondary">
                0 = no filter; only applies to sessions with an event type set
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                If an image has no Event Fit Score, Step 4 falls back to Album Worthy. Non-worthy images are excluded when this slider is above 0.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Min Persons"
                type="number"
                size="small"
                fullWidth
                value={qualityFilters.minPersonCount ?? ''}
                onChange={(e) =>
                  setQualityFilters((f) => ({ ...f, minPersonCount: e.target.value ? parseInt(e.target.value) : null }))
                }
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Max Persons"
                type="number"
                size="small"
                fullWidth
                value={qualityFilters.maxPersonCount ?? ''}
                onChange={(e) =>
                  setQualityFilters((f) => ({ ...f, maxPersonCount: e.target.value ? parseInt(e.target.value) : null }))
                }
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                Event Types to Include
              </Typography>
              {sessionEventTypes.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  No event type data yet — run AI Analysis (Step 3) first.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {sessionEventTypes.map((type) => {
                    const isExcluded = qualityFilters.excludedEventTypes.includes(type);
                    return (
                      <Chip
                        key={type}
                        label={type.charAt(0).toUpperCase() + type.slice(1)}
                        size="small"
                        variant={isExcluded ? 'outlined' : 'filled'}
                        color={isExcluded ? 'default' : 'primary'}
                        onClick={() =>
                          setQualityFilters((f) => ({
                            ...f,
                            excludedEventTypes: isExcluded
                              ? f.excludedEventTypes.filter((t) => t !== type)
                              : [...f.excludedEventTypes, type],
                          }))
                        }
                        sx={{
                          opacity: isExcluded ? 0.45 : 1,
                          cursor: 'pointer',
                          textDecoration: isExcluded ? 'line-through' : 'none',
                        }}
                      />
                    );
                  })}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                All types included by default — click a type to exclude it.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Button
                size="small"
                onClick={() =>
                  setQualityFilters({
                    excludeBlurry: true, requireAlbumWorthy: true, requireSmile: false,
                    minQualityScore: 0, minEventFitScore: 5, excludedEventTypes: [], minPersonCount: null, maxPersonCount: null,
                  })
                }
              >
                Reset to Defaults
              </Button>
              <Button
                size="small"
                sx={{ ml: 1 }}
                onClick={() =>
                  setQualityFilters({
                    excludeBlurry: false, requireAlbumWorthy: false, requireSmile: false,
                    minQualityScore: 0, minEventFitScore: 0, excludedEventTypes: [], minPersonCount: null, maxPersonCount: null,
                  })
                }
              >
                Clear All Filters
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* ── Run button ───────────────────────────────────────────────────────── */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={running ? <CircularProgress size={18} color="inherit" /> : <ClusterIcon />}
          onClick={handleRunClustering}
          disabled={running || !canCluster}
        >
          {running
            ? 'Clustering…'
            : hasClusters
            ? 'Re-run Clustering'
            : 'Run Clustering'}
        </Button>
        {running && (
          <Typography variant="body2" color="text.secondary">
            This may take a moment for large sessions…
          </Typography>
        )}
      </Stack>

      {/* ── Run error ────────────────────────────────────────────────────────── */}
      {runError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {runError}
        </Alert>
      )}

      {/* ── Cluster results ───────────────────────────────────────────────────── */}
      {loadingClusters ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : clusters && clusters.length > 0 ? (
        <Box sx={{ mb: 3 }}>
          {/* Section header with collapse toggle */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
              Clusters ({clusters.length})
            </Typography>
            <Tooltip title={clustersOpen ? 'Collapse cluster list' : 'Show cluster details & thumbnails'}>
              <Button
                size="small"
                variant="outlined"
                startIcon={clustersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setClustersOpen((v) => !v)}
              >
                {clustersOpen ? 'Collapse' : 'Show details'}
              </Button>
            </Tooltip>
          </Box>
          <Collapse in={clustersOpen}>
            {clusters.map((cluster, index) => {
              const isExpanded = expandedClusters.has(cluster.clusterId);
              return (
                <Accordion
                  key={cluster.clusterId}
                  disableGutters
                  expanded={isExpanded}
                  onChange={(_, open) =>
                    setExpandedClusters((prev) => {
                      const next = new Set(prev);
                      open ? next.add(cluster.clusterId) : next.delete(cluster.clusterId);
                      return next;
                    })
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', flexWrap: 'wrap' }}>
                      <Chip
                        label={index + 1}
                        size="small"
                        color="primary"
                        sx={{ minWidth: 32, fontWeight: 'bold' }}
                      />
                      <Typography variant="body1" sx={{ flexGrow: 1 }}>
                        {cluster.clusterLabel || cluster.clusterId}
                      </Typography>
                      <Chip
                        size="small"
                        label={`${cluster.imageCount ?? cluster.images?.length ?? 0} photo${(cluster.imageCount ?? cluster.images?.length ?? 0) !== 1 ? 's' : ''}`}
                        variant="outlined"
                      />
                      {cluster.sceneType && (
                        <Chip size="small" label={cluster.sceneType} color="secondary" variant="outlined" />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {/* Common tags */}
                    {cluster.commonTags?.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                        {cluster.commonTags.map((tag) => (
                          <Chip key={tag} label={tag} size="small" variant="outlined" />
                        ))}
                      </Box>
                    )}
                    {/* Time range */}
                    {cluster.timeRange && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                        {new Date(cluster.timeRange.start).toLocaleDateString('en-GB')} –{' '}
                        {new Date(cluster.timeRange.end).toLocaleDateString('en-GB')}
                      </Typography>
                    )}
                    {/* Thumbnails — only fetched when this accordion is open */}
                    {isExpanded && (
                      <Grid container spacing={1}>
                        {(cluster.images || []).map((img) => {
                          const imageId = img.imageId || img.uniqueId;
                          return (
                            <Grid item xs={6} sm={4} md={3} key={imageId}>
                              <AuthenticatedImage
                                imageId={imageId}
                                type="thumbnail"
                                alt={img.filename || imageId}
                                onClick={() => setImageDetailDialog({ open: true, imageId })}
                                sx={{ width: '100%', height: 100, objectFit: 'contain', borderRadius: 1, display: 'block', bgcolor: 'grey.50', cursor: 'pointer', '&:hover': { opacity: 0.8, outline: '2px solid', outlineColor: 'primary.main' } }}
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Collapse>
        </Box>
      ) : clusters !== null && clusters.length === 0 && !running ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          No clusters yet. Click <strong>Run Clustering</strong> to group your photos.
        </Alert>
      ) : null}

      {/* ── Unclustered images ──────────────────────────────────────────── */}
      {unclusteredImages && unclusteredImages.length > 0 && (
        <Accordion disableGutters variant="outlined" sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Chip
                label="?"
                size="small"
                color="default"
                sx={{ minWidth: 32, fontWeight: 'bold' }}
              />
              <Typography variant="body1" sx={{ flexGrow: 1 }}>
                Unclustered
              </Typography>
              <Chip
                size="small"
                label={`${unclusteredImages.length} photo${unclusteredImages.length !== 1 ? 's' : ''}`}
                color="warning"
                variant="filled"
                sx={{ mr: 3 }}
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              These images have embeddings but were not assigned to any cluster. Try lowering the
              similarity threshold or minimum cluster size to include them.
            </Typography>
            {/* Pagination controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Per page</InputLabel>
                <Select
                  value={unclusteredPageSize}
                  label="Per page"
                  onChange={(e) => { setUnclusteredPageSize(e.target.value); setUnclusteredPage(0); }}
                >
                  {[25, 50, 75, 100].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                </Select>
              </FormControl>
              <TablePagination
                component="div"
                count={unclusteredImages.length}
                page={unclusteredPage}
                rowsPerPage={unclusteredPageSize}
                rowsPerPageOptions={[]}
                onPageChange={(_, p) => setUnclusteredPage(p)}
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
              />
            </Box>
            <Grid container spacing={1}>
              {unclusteredImages.slice(unclusteredPage * unclusteredPageSize, (unclusteredPage + 1) * unclusteredPageSize).map((img) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={img.imageId}>
                  <AuthenticatedImage
                    imageId={img.imageId}
                    type="thumbnail"
                    alt={img.filename || img.imageId}
                    onClick={() => setImageDetailDialog({ open: true, imageId: img.imageId })}
                    sx={{ width: '100%', height: 100, objectFit: 'contain', borderRadius: 1, display: 'block', bgcolor: 'grey.50', cursor: 'pointer', '&:hover': { opacity: 0.8, outline: '2px solid', outlineColor: 'primary.main' } }}
                  />
                </Grid>
              ))}
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {/* ── Navigation ───────────────────────────────────────────────────────── */}
      {hasClusters ? (
        <Alert severity="success" icon={<CheckCircle />} sx={{ mt: 2, py: 0.5 }}>
          {totalClusters} cluster{totalClusters !== 1 ? 's' : ''} ready — proceed to generate the album.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mt: 2, py: 0.5 }}>
          Run clustering or skip to continue without grouped sections.
        </Alert>
      )}

      {/* ── Image detail dialog ──────────────────────────────────────────────── */}
      <ImageAnalysisDetailsDialog
        open={imageDetailDialog.open}
        onClose={() => setImageDetailDialog({ open: false, imageId: null })}
        imageId={imageDetailDialog.imageId}
      />

      {/* ── Snackbar ─────────────────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WizardStep4Clustering;
